/* Optimize Minerals — index nav.

   Drives blocks/_op-index-nav.liquid: marks the entry for whatever section is
   being read, jumps to a section clear of the sticky header when one is
   clicked, and keeps the marked chip visible in the mobile rail.

   Marking is done from scroll position rather than from IntersectionObserver
   entries alone. An observer tells you which targets are *on screen*, which on
   a long reference page is usually three or four at once and, past the last
   heading, none at all — neither answers "which one am I reading". So the
   observer here is used only as a cheap trigger: it wakes the handler when the
   set of visible targets changes, and the handler then picks the last target
   whose top has passed the reading line. That is one answer, it is always
   defined, and it never flickers between two neighbours.

   Deliberately no hash writing. Pushing #anchor on every scroll fills the back
   button with the page's own table of contents. */
(function () {
  'use strict';

  var READING_LINE = 0.3; /* fraction of the viewport height */

  function setup(nav) {
    if (nav.dataset.opIndexReady === '1') return;
    nav.dataset.opIndexReady = '1';

    var links = Array.prototype.slice.call(
      nav.querySelectorAll('[data-op-index-link]')
    );
    if (!links.length) return;

    /* Resolved once. A link whose target is missing — an anchor renamed on one
       side of the pipe and not the other — is dropped rather than left to
       throw on every scroll. */
    var entries = links
      .map(function (link) {
        var id = (link.getAttribute('href') || '').slice(1);
        var target = id ? document.getElementById(id) : null;
        if (!target) {
          link.classList.add('op-idx__link--orphan');
          return null;
        }
        return { link: link, target: target };
      })
      .filter(Boolean);

    if (!entries.length) return;

    var offset = parseInt(nav.style.getPropertyValue('--op-idx-top'), 10) || 100;
    var current = null;

    function mark() {
      var line = window.innerHeight * READING_LINE;
      var found = null;

      for (var i = 0; i < entries.length; i++) {
        if (entries[i].target.getBoundingClientRect().top <= line) {
          found = entries[i];
        }
      }
      /* Above the first target, the first entry is the honest answer: that is
         the section the reader is heading into. */
      if (!found) found = entries[0];
      if (found === current) return;

      if (current) current.link.classList.remove('op-idx__link--on');
      found.link.classList.add('op-idx__link--on');
      found.link.setAttribute('aria-current', 'true');
      if (current) current.link.removeAttribute('aria-current');
      current = found;

      keepInView(found.link);
    }

    /* Only while the list is a sideways rail. On desktop the column is pinned
       and tall, and hijacking its scroll position would fight the reader. */
    function keepInView(link) {
      var list = nav.querySelector('.op-idx__list');
      if (!list) return;
      if (list.scrollWidth <= list.clientWidth + 1) return;

      var want = link.offsetLeft - list.clientWidth / 2 + link.offsetWidth / 2;
      list.scrollTo({
        left: Math.max(0, want),
        behavior: prefersReducedMotion() ? 'instant' : 'smooth'
      });
    }

    function prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    /* Scrolled by hand rather than left to CSS scroll-margin: the offset is a
       block setting, and writing it onto every target from here would mean
       reaching outside this block to restyle elements it does not own. */
    entries.forEach(function (entry) {
      entry.link.addEventListener('click', function (event) {
        if (event.metaKey || event.ctrlKey || event.shiftKey) return;
        event.preventDefault();
        var top =
          entry.target.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({
          top: Math.max(0, top),
          /* 'instant', never 'auto'. `auto` means "use the scroll-behavior CSS
             property", and this theme sets `html { scroll-behavior: smooth }` -
             so `auto` animated the jump anyway and the reduced-motion branch
             did nothing at all. */
          behavior: prefersReducedMotion() ? 'instant' : 'smooth'
        });
      });
    });

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        ticking = false;
        mark();
      });
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    mark();

    /* Arriving on a hash — a shared link to one topic.

       The browser makes its own jump, but it makes it before the images and
       lazily-revealed copy further down have taken their final heights. On a
       page this long that lands hundreds of pixels short of the heading, and
       then the content settles around the wrong spot. Re-applying it once
       `load` has fired is the only reliable way to land on the right topic,
       and going through the same offset as a click means an arrival and a jump
       put the heading in the same place.

       Skipped the moment somebody has started scrolling for themselves: a page
       yanking itself somewhere else a second after you began reading is worse
       than a link that lands imprecisely. */
    var userMoved = false;
    ['wheel', 'touchstart', 'keydown'].forEach(function (evt) {
      window.addEventListener(evt, function () { userMoved = true; }, {
        passive: true,
        once: true
      });
    });

    function honourHash() {
      if (userMoved) return;
      var id = (window.location.hash || '').slice(1);
      if (!id) return;
      var match = entries.filter(function (e) { return e.target.id === id; })[0];
      if (!match) return;
      window.scrollTo({
        top: Math.max(
          0,
          match.target.getBoundingClientRect().top + window.pageYOffset - offset
        ),
        /* Instant: an arrival is where the page should already be, not a
           journey. Left on `auto` this inherited the theme's smooth scrolling
           and spent several seconds travelling the length of the page, and any
           touch of the wheel on the way cancelled it. */
        behavior: 'instant'
      });
      mark();
    }

    if (document.readyState === 'complete') {
      honourHash();
    } else {
      window.addEventListener('load', honourHash);
    }
  }

  function init(root) {
    (root || document)
      .querySelectorAll('[data-op-index-nav]')
      .forEach(setup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  /* The theme editor rebuilds a block's markup in place, which leaves the old
     listeners bound to elements no longer in the document and the new nav
     unwired. Re-running setup on the replacement is enough — the guard flag
     lives on the element, so it comes back false. */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
  document.addEventListener('shopify:block:select', function (event) {
    init(event.target.parentNode || document);
  });
})();
